import React, { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { listEdgeDevices, listDeviceTags, getEdgeDataLatest } from "../../api/dashboardApi";
import { buildLatestMap, deviceSummary, mergeDeviceTags, formatAgo } from "./assetHelpers";

// Small cloud icon: filled/blue when the device has fresh data (last 5 min), outline/grey otherwise.
function CloudIcon({ fresh }) {
  const color = fresh ? "var(--accent-navy-light, #3b82f6)" : "var(--text-muted)";
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill={fresh ? color : "none"} stroke={color} strokeWidth="1.8">
      <path d="M6.5 19a4.5 4.5 0 0 1-.4-8.98A5.5 5.5 0 0 1 17 10.5a4 4 0 0 1-.5 7.98z" />
    </svg>
  );
}

const STATUS_COLOR = { fresh: "#3fb950", stale: "#f0883e", none: "var(--text-muted)" };

export default function AssetsTab({ edgeId, accessToken }) {
  const { t } = useTranslation();
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [latestMap, setLatestMap] = useState(new Map());
  const [selectedId, setSelectedId] = useState(null);
  const [tagsByDevice, setTagsByDevice] = useState({}); // device_id -> full tag list
  const [tagsLoading, setTagsLoading] = useState(false);

  const load = useCallback(async () => {
    if (!accessToken || !edgeId) return;
    setLoading(true);
    try {
      const devs = await listEdgeDevices(accessToken, edgeId);
      setDevices(devs || []);
      const ids = (devs || []).map((d) => d.id);
      if (ids.length) {
        const latest = await getEdgeDataLatest(accessToken, edgeId, ids);
        setLatestMap(buildLatestMap(Array.isArray(latest) ? latest : []));
      } else {
        setLatestMap(new Map());
      }
      setSelectedId((prev) => prev || (devs && devs[0]?.id) || null);
    } catch (_) {
      setDevices([]);
      setLatestMap(new Map());
    } finally {
      setLoading(false);
    }
  }, [accessToken, edgeId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!selectedId || tagsByDevice[selectedId] || !accessToken || !edgeId) return;
    setTagsLoading(true);
    listDeviceTags(accessToken, edgeId, selectedId)
      .then((tags) => setTagsByDevice((prev) => ({ ...prev, [selectedId]: tags || [] })))
      .catch(() => setTagsByDevice((prev) => ({ ...prev, [selectedId]: [] })))
      .finally(() => setTagsLoading(false));
  }, [selectedId, tagsByDevice, accessToken, edgeId]);

  if (loading) {
    return <div style={{ padding: "32px", color: "var(--text-muted)", textAlign: "center" }}>{t("edges.assets.loading")}</div>;
  }

  if (devices.length === 0) {
    return (
      <div style={{ padding: "32px", textAlign: "center", color: "var(--text-muted)" }}>
        {t("edges.assets.noDevices")}
      </div>
    );
  }

  const selectedTags = mergeDeviceTags(tagsByDevice[selectedId], latestMap.get(selectedId), Date.now());

  return (
    <div style={styles.wrap}>
      <div style={styles.list}>
        {devices.map((d) => {
          const { fresh, count } = deviceSummary(latestMap.get(d.id), Date.now());
          const active = d.id === selectedId;
          return (
            <button
              key={d.id}
              style={active ? styles.deviceRowActive : styles.deviceRow}
              onClick={() => setSelectedId(d.id)}
            >
              <CloudIcon fresh={fresh} />
              <div style={{ textAlign: "left" }}>
                <div style={{ color: "var(--text-primary)", fontSize: "14px" }}>{d.name}</div>
                <div style={{ color: "var(--text-muted)", fontSize: "12px" }}>
                  {count > 0 ? t("edges.assets.tagCount", { count }) : t("edges.assets.noTagData")}
                </div>
              </div>
            </button>
          );
        })}
      </div>

      <div style={styles.detail}>
        {!selectedId ? (
          <div style={{ color: "var(--text-muted)" }}>{t("edges.assets.pickDevice")}</div>
        ) : tagsLoading && !tagsByDevice[selectedId] ? (
          <div style={{ color: "var(--text-muted)" }}>{t("edges.assets.tagsLoading")}</div>
        ) : selectedTags.length === 0 ? (
          <div style={{ color: "var(--text-muted)" }}>{t("edges.assets.noTags")}</div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
            <thead>
              <tr style={{ backgroundColor: "var(--bg-surface)" }}>
                {["colTag", "colValue", "colUnit", "colLastRead", "colStatus"].map((h) => (
                  <th
                    key={h}
                    style={{
                      padding: "10px 14px",
                      textAlign: "left",
                      color: "var(--text-muted)",
                      borderBottom: "1px solid var(--border)",
                      fontWeight: 600,
                    }}
                  >
                    {t(`edges.assets.${h}`)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {selectedTags.map((tag) => (
                <tr key={tag.id} style={{ borderBottom: "1px solid var(--border)" }}>
                  <td style={{ padding: "9px 14px", color: "var(--text-primary)" }}>{tag.label || tag.name}</td>
                  <td style={{ padding: "9px 14px", color: "var(--text-primary)", fontWeight: 600 }}>
                    {tag.value != null ? tag.value : "—"}
                  </td>
                  <td style={{ padding: "9px 14px", color: "var(--text-muted)" }}>{tag.unit || "—"}</td>
                  <td style={{ padding: "9px 14px", color: "var(--text-muted)", fontSize: "12px" }}>
                    {formatAgo(tag.time)}
                  </td>
                  <td style={{ padding: "9px 14px", color: STATUS_COLOR[tag.statusKey], fontWeight: 600 }}>{tag.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

const styles = {
  wrap: { display: "flex", gap: "16px", color: "var(--text-primary)" },
  list: {
    width: "260px",
    flexShrink: 0,
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    maxHeight: "480px",
    overflowY: "auto",
  },
  deviceRow: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "10px 12px",
    borderRadius: "8px",
    border: "1px solid var(--border)",
    backgroundColor: "transparent",
    cursor: "pointer",
  },
  deviceRowActive: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "10px 12px",
    borderRadius: "8px",
    border: "1px solid var(--accent-navy-light)",
    backgroundColor: "var(--bg-hover)",
    cursor: "pointer",
  },
  detail: { flex: 1, minWidth: 0 },
};
